import type { CardId, Group } from '@rummy/engine';

export interface TrayGeometry {
  cardWidth: number;
  cardHeight: number;
  /** Visible separation between lanes (§6.3 — "groups as lanes"). */
  laneGap: number;
  /** Available width for the whole tray. */
  width: number;
  minStep: number;
  maxStep: number;
  /** Vertical space between two rows of lanes: room for the lane labels. */
  rowGap: number;
}

/** How much of each card the fan leaves showing, as a fraction of card width.
 *  Must clear a two-glyph index — a fan that clips "10" is unreadable. */
const MIN_STEP_RATIO = 0.46;
const MAX_STEP_RATIO = 0.62;
const GAP_RATIO = 0.3;
/*
 * This is a layout floor, not a preferred card size. A 34px floor made a
 * multi-lane hand wider than a narrow phone tray, leaving the final cards
 * beyond the right edge. The face remains readable at 24px because the fan
 * still exposes 46% of each card; keeping every card reachable wins over
 * preserving a desktop-sized minimum in that constrained case.
 */
const MIN_CARD_WIDTH = 24;
const MAX_CARD_WIDTH = 78;
/**
 * Two rows only earn their height if they make the cards clearly bigger. A
 * sorted hand gains 85–100%; an unsorted hand plus the card just drawn gains
 * under 20% and would spend a whole row on one card, so it stays on one.
 */
const TWO_ROW_GAIN = 1.3;

/** How wide a row is, in card widths, at the tightest readable fan. */
function widthUnits(cards: number, lanes: number): number {
  const l = Math.max(1, lanes);
  return Math.max(0, cards - l) * MIN_STEP_RATIO + l + Math.max(0, l - 1) * GAP_RATIO;
}

/**
 * Solves the card size from the space available.
 *
 * Sizing by breakpoint gets this backwards: it fixes the card and squeezes the
 * step until the indices collide, which is exactly what happens to a
 * thirteen-card hand on a phone. Here the step is pinned to a fraction of the
 * card and the card shrinks until the whole tray fits, so the hand stays
 * readable at any width.
 */
export function solveGeometry(
  width: number,
  cards: number,
  lanes: number,
  /** Ceiling imposed by the space above the tray — a phone in landscape has
   *  plenty of width and almost no height. */
  maxCardHeight = Infinity,
): TrayGeometry {
  const denominator = widthUnits(Math.max(1, cards), lanes);
  const byHeight = Math.floor(maxCardHeight / 1.41);
  const cardWidth = Math.max(
    MIN_CARD_WIDTH,
    Math.min(MAX_CARD_WIDTH, byHeight, Math.floor((width - 12) / denominator)),
  );
  const cardHeight = Math.round(cardWidth * 1.41);
  return {
    cardWidth,
    cardHeight,
    laneGap: Math.round(cardWidth * GAP_RATIO),
    // Clears the first row's underline and the second row's labels, which
    // sit above their cards.
    rowGap: Math.max(32, Math.round(cardHeight * 0.26)),
    width,
    // Round the exposed sliver up: rounding down can turn 46% into 44.8% on
    // a 29px card and clip the "10" index at the narrowest breakpoints.
    minStep: Math.ceil(cardWidth * MIN_STEP_RATIO),
    maxStep: Math.round(cardWidth * MAX_STEP_RATIO),
  };
}

/**
 * Where a hand breaks onto a second row: the number of lanes on the first.
 *
 * Lanes are never split. A group is one thing the player made, and a group
 * that continued on the next line would read as two. So the break falls
 * between lanes, at whichever boundary leaves the two rows closest in width.
 */
export function splitRows(groups: readonly Group[]): number {
  const lanes = groups.filter((g) => g.length > 0);
  if (lanes.length < 2) return lanes.length;
  let best = 1;
  let bestWidth = Infinity;
  for (let k = 1; k < lanes.length; k++) {
    const top = lanes.slice(0, k);
    const bottom = lanes.slice(k);
    const widest = Math.max(
      widthUnits(top.reduce((n, g) => n + g.length, 0), top.length),
      widthUnits(bottom.reduce((n, g) => n + g.length, 0), bottom.length),
    );
    if (widest < bestWidth) {
      bestWidth = widest;
      best = k;
    }
  }
  return best;
}

/**
 * Sizes a hand that may use two rows.
 *
 * A portrait phone is narrow and tall: thirteen cards in one row come out
 * 37px wide on a small phone while the felt above them sits empty. Two rows of
 * lanes double the card size there. They are only taken when they buy at
 * least 30% — a hand still in one long group gains little from a second row
 * and would just give up height.
 *
 * Returns the geometry and, when two rows win, the lane count of the first.
 */
export function solveRows(
  width: number,
  groups: readonly Group[],
  maxCardHeight: number,
  twoRowMaxCardHeight: number | null,
): { geom: TrayGeometry; split: number | null } {
  const lanes = groups.filter((g) => g.length > 0);
  const cards = lanes.reduce((n, g) => n + g.length, 0);
  const one = solveGeometry(width, cards, lanes.length, maxCardHeight);
  if (twoRowMaxCardHeight === null || lanes.length < 2) return { geom: one, split: null };
  const split = splitRows(lanes);
  const top = lanes.slice(0, split);
  const bottom = lanes.slice(split);
  const topCards = top.reduce((n, g) => n + g.length, 0);
  const bottomCards = bottom.reduce((n, g) => n + g.length, 0);
  const wider =
    widthUnits(topCards, top.length) >= widthUnits(bottomCards, bottom.length)
      ? { cards: topCards, lanes: top.length }
      : { cards: bottomCards, lanes: bottom.length };
  const two = solveGeometry(width, wider.cards, wider.lanes, twoRowMaxCardHeight);
  return two.cardWidth >= one.cardWidth * TWO_ROW_GAIN ? { geom: two, split } : { geom: one, split: null };
}

/**
 * A card's place in the tray.
 *
 * Position only — no rotation and no arc. A curved fan was tried and removed:
 * it reads as decoration on a screen whose whole job is letting you compare
 * thirteen cards at a glance, and tilted cards make that comparison harder, not
 * easier. The tray stays flat and square on purpose.
 */
export interface CardSlot {
  id: CardId;
  x: number;
  /** Top of the card's row: 0 on the first row. */
  y: number;
  lane: number;
  index: number;
}

export interface LaneBox {
  lane: number;
  start: number;
  end: number;
  count: number;
  row: number;
  y: number;
}

export interface TrayLayout {
  slots: Map<CardId, CardSlot>;
  lanes: LaneBox[];
  step: number;
  contentWidth: number;
  offsetX: number;
  rows: number;
  /** Distance from the top of one row to the top of the next. */
  rowHeight: number;
}

/** Desktop centres the hand; phones bias it gently toward the leading edge. */
export type TrayAlignment = 'center' | 'mobile-leading';

/**
 * Lays the tray out as absolute positions rather than as DOM order.
 *
 * Everything downstream depends on this: because a card's position is a pure
 * function of the grouping, reordering is a state change and the animation is a
 * CSS transition on `transform`. There is no FLIP bookkeeping, no measuring
 * between frames, and no chance of the visual order disagreeing with the model.
 */
export function layoutTray(
  groups: readonly Group[],
  geom: TrayGeometry,
  alignment: TrayAlignment = 'center',
  /** Lanes on the first row when the hand uses two; `null` for one row. */
  split: number | null = null,
): TrayLayout {
  const lanes = groups.filter((g) => g.length > 0);
  const rows =
    split !== null && split > 0 && split < lanes.length
      ? [lanes.slice(0, split), lanes.slice(split)]
      : [lanes];
  const rowHeight = geom.cardHeight + geom.rowGap;

  // One step for the whole hand, set by the row that needs it tightest, so the
  // two rows of a split hand fan the same way.
  let step = geom.maxStep;
  for (const row of rows) {
    const laneCount = row.length;
    const cardCount = row.reduce((n, g) => n + g.length, 0);
    if (cardCount > laneCount) {
      const fixed = geom.cardWidth * laneCount + geom.laneGap * Math.max(0, laneCount - 1);
      step = Math.min(step, (geom.width - fixed) / (cardCount - laneCount));
    }
  }
  step = Math.max(geom.minStep, Math.min(geom.maxStep, step));

  const slots = new Map<CardId, CardSlot>();
  const boxes: LaneBox[] = [];
  let contentWidth = 0;
  let firstOffset = 0;
  let lane = 0;

  rows.forEach((row, r) => {
    const laneCount = row.length;
    const cardCount = row.reduce((n, g) => n + g.length, 0);
    const width =
      step * (cardCount - laneCount) +
      geom.cardWidth * laneCount +
      geom.laneGap * Math.max(0, laneCount - 1);
    contentWidth = Math.max(contentWidth, width);
    const spareWidth = Math.max(0, geom.width - width);
    // Phones are easier to play with a slight leading bias, but pinning the
    // hand to the edge looks unbalanced. Desktop keeps the conventional
    // centred hand.
    const offsetX = alignment === 'mobile-leading' ? spareWidth * 0.28 : spareWidth / 2;
    if (r === 0) firstOffset = offsetX;
    const y = r * rowHeight;
    let x = offsetX;

    row.forEach((group) => {
      const start = x;
      group.forEach((id, index) => {
        slots.set(id, { id, x, y, lane, index });
        x += step;
      });
      // The last card in a lane occupies its full width, not just a step.
      x += geom.cardWidth - step;
      boxes.push({ lane, start, end: x, count: group.length, row: r, y });
      x += geom.laneGap;
      lane++;
    });
  });

  return { slots, lanes: boxes, step, contentWidth, offsetX: firstOffset, rows: rows.length, rowHeight };
}

export type DropTarget =
  | { kind: 'into'; lane: number; index: number }
  | { kind: 'new-lane'; before: number };

/**
 * Resolves a pointer position to an insertion point.
 *
 * Dropping *between* lanes creates a new one. That is the whole mechanism for
 * making a group: there is no "new group" button to find, and no modal.
 *
 * `pointerY` is measured from the top of the first row and only matters when
 * the hand is on two rows: it picks the row, and the rest is the same
 * left-to-right search within it.
 */
export function resolveDropTarget(
  pointerX: number,
  layout: TrayLayout,
  groups: readonly Group[],
  geom: TrayGeometry,
  pointerY = 0,
): DropTarget {
  const lanes = groups.filter((g) => g.length > 0);
  if (layout.lanes.length === 0) return { kind: 'new-lane', before: 0 };

  const row =
    layout.rows > 1 && pointerY > layout.rowHeight - geom.rowGap / 2 ? layout.rows - 1 : 0;
  const inRow = layout.lanes.filter((box) => box.row === row);
  const first = inRow[0]!;
  const last = inRow[inRow.length - 1]!;
  if (pointerX < first.start - geom.laneGap * 0.5) return { kind: 'new-lane', before: first.lane };
  if (pointerX > last.end + geom.laneGap * 0.5) {
    return { kind: 'new-lane', before: last.lane + 1 };
  }

  for (let i = first.lane; i <= last.lane; i++) {
    const box = layout.lanes[i]!;
    if (pointerX >= box.start && pointerX <= box.end) {
      const group = lanes[i] ?? [];
      let index = group.length;
      for (let c = 0; c < group.length; c++) {
        const slot = layout.slots.get(group[c]!);
        if (!slot) continue;
        const centre = slot.x + (c === group.length - 1 ? geom.cardWidth : layout.step) / 2;
        if (pointerX < centre) {
          index = c;
          break;
        }
      }
      return { kind: 'into', lane: i, index };
    }
    const next = layout.lanes[i + 1];
    if (next && next.row === row && pointerX > box.end && pointerX < next.start) {
      return { kind: 'new-lane', before: i + 1 };
    }
  }
  return { kind: 'into', lane: last.lane, index: last.count };
}

/** Removes a set of cards from the grouping, dropping any lane left empty. */
export function withoutCards(groups: readonly Group[], cards: readonly CardId[]): Group[] {
  const drop = new Set(cards);
  return groups.map((g) => g.filter((c) => !drop.has(c))).filter((g) => g.length > 0);
}

export function insertCards(
  groups: readonly Group[],
  cards: readonly CardId[],
  target: DropTarget,
): Group[] {
  const next = groups.map((g) => [...g]);
  if (target.kind === 'new-lane') {
    next.splice(target.before, 0, [...cards]);
    return next;
  }
  const lane = next[target.lane] ?? [];
  lane.splice(target.index, 0, ...cards);
  next[target.lane] = lane;
  return next;
}

export function moveCards(
  groups: readonly Group[],
  cards: readonly CardId[],
  target: DropTarget,
): Group[] {
  return insertCards(withoutCards(groups, cards), cards, target);
}

/**
 * Reconciles the local arrangement against the authoritative hand: keeps the
 * player's lanes, drops cards they no longer hold, and appends anything new
 * (the card they just drew) as its own lane so it arrives in a held state
 * rather than silently buried in an existing group.
 */
export function reconcileGroups(
  groups: readonly Group[],
  hand: readonly CardId[],
  options: { newCardLane?: boolean } = {},
): Group[] {
  const inHand = new Set(hand);
  const placed = new Set<CardId>();
  const kept: Group[] = [];
  for (const group of groups) {
    const lane = group.filter((id) => {
      if (!inHand.has(id) || placed.has(id)) return false;
      placed.add(id);
      return true;
    });
    if (lane.length > 0) kept.push(lane);
  }
  const missing = hand.filter((id) => !placed.has(id));
  if (missing.length > 0) {
    if (options.newCardLane) kept.push(missing);
    else if (kept.length > 0) kept[kept.length - 1]!.push(...missing);
    else kept.push(missing);
  }
  return kept;
}
